import { useEffect, useId, useState } from 'react';
import './NumberStepper.css';

/**
 * A whole number with a minus and a plus button (owner requests, 2026-10-09: bottles drunk in
 * "Boire et noter", purchase quantities), from `min` to `max`. The number can also be typed;
 * it is kept within the bounds, and a cleared box goes back to the last number on leaving it.
 */
export function NumberStepper({
  label,
  value,
  min = 1,
  max,
  decreaseLabel,
  increaseLabel,
  onChange,
  id,
}: {
  label: string;
  value: number;
  min?: number;
  max?: number;
  decreaseLabel: string;
  increaseLabel: string;
  onChange: (value: number) => void;
  /** The box's id, for a form that refers to it; one is made up otherwise. */
  id?: string;
}) {
  const madeUp = useId();
  const inputId = id ?? madeUp;
  const [draft, setDraft] = useState(String(value));
  useEffect(() => setDraft(String(value)), [value]);
  const clamp = (n: number) => Math.max(min, max !== undefined ? Math.min(max, n) : n);

  return (
    <div className="number-stepper">
      <label className="number-stepper__label" htmlFor={inputId}>
        {label}
      </label>
      <button
        type="button"
        aria-label={decreaseLabel}
        aria-controls={inputId}
        disabled={value <= min}
        onClick={() => onChange(clamp(value - 1))}
      >
        −
      </button>
      <input
        id={inputId}
        className="number-stepper__value"
        inputMode="numeric"
        value={draft}
        onChange={(event) => {
          const digits = event.target.value.replace(/\D/g, '');
          if (digits === '') {
            setDraft('');
            return;
          }
          const next = clamp(Number(digits));
          setDraft(String(next));
          onChange(next);
        }}
        onBlur={() => setDraft(String(value))}
      />
      <button
        type="button"
        aria-label={increaseLabel}
        aria-controls={inputId}
        disabled={max !== undefined && value >= max}
        onClick={() => onChange(clamp(value + 1))}
      >
        +
      </button>
    </div>
  );
}
